import NoAttachmentsIcon from '@assets/images/icons/no-attachments.svg?react'
import { DELETE_FILE } from '@components/App/SystemDialogs/constants'
import { Button } from '@octopus-do/octopus-storybook'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { StorageActions } from '@store/reducers/manager/storage'
import { openSystemDialog } from '@storeOld/reducers/ui'
import cx from 'classnames'
import type React from 'react'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { deleteProjectAttachment } from '@/services/project'
import type { AttachmentItem } from '@/types/models'
import css from './AttachedFiles.module.scss'
import List from './components/List/List'

type Props = {}

const AttachedFiles: React.FC<Props> = () => {
  const dispatch = useDispatch()
  const storage = useSelector(migrationProjectSelector.getProjectStorage)

  const [loading, setLoading] = useState<boolean>(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const selectedIdsSet = new Set(selectedIds)
  const storageItems = storage.items
  const isAllListSelected = selectedIdsSet.size === storageItems.length

  const containerClassName = cx(css.container, {
    [css.showFooter]: selectedIdsSet.size > 0,
    [css.noAttachContainer]: storageItems.length === 0,
  })

  const handleCloseModal = () => {
    setSelectedIds([])
  }

  const handleSelectItem = (file: AttachmentItem) => {
    const existIndex = selectedIds.findIndex((id) => id === file.id)

    const payload =
      existIndex === -1
        ? [...selectedIds, file.id]
        : [
            ...selectedIds.slice(0, existIndex),
            ...selectedIds.slice(existIndex + 1),
          ]

    setSelectedIds(payload)
  }

  const handleDeleteSelected = () => {
    dispatch(
      openSystemDialog(DELETE_FILE, {
        filesLength: selectedIdsSet.size,
        onDelete: async () => {
          setLoading(true)

          const payload = storageItems.filter((item) =>
            selectedIdsSet.has(item.id)
          )

          await Promise.all(
            payload.map((item) => deleteProjectAttachment(item.id))
          )

          dispatch(StorageActions.removeAttachments(payload))
          setLoading(false)

          // Unselect deleted files
          setSelectedIds([])
        },
      })
    )
  }

  const handleUnSelectAll = () => {
    setSelectedIds([])
  }

  const handleSelectAll = () => {
    setSelectedIds(storageItems.map((item) => item.id))
  }

  return (
    <>
      <div className={containerClassName}>
        {storageItems.length ? (
          <>
            <div className={css.description}>
              These files are used in the project as attachments. If you delete
              them, the attachments will become inactive.
            </div>

            <div
              className={css.selectAll}
              onClick={isAllListSelected ? handleUnSelectAll : handleSelectAll}
            >
              {isAllListSelected ? 'Uns' : 'S'}elect all
            </div>

            <List
              selectedSet={selectedIdsSet}
              files={storageItems}
              onClick={handleSelectItem}
              isScrollable={storageItems.length > 6}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
            />
          </>
        ) : (
          <div className={css.noAttach}>
            <div className={css.icon}>
              <NoAttachmentsIcon />
            </div>
            <div className={css.description}>
              The project doesn't contain any files
            </div>
          </div>
        )}
      </div>

      {selectedIdsSet.size > 0 && (
        <div className={css.footer}>
          <Button
            tone="danger"
            size="medium"
            label="Delete"
            loading={loading}
            onClick={handleDeleteSelected}
          />
          <Button
            priority="secondary"
            size="medium"
            label="Cancel"
            onClick={handleCloseModal}
          />
        </div>
      )}
    </>
  )
}

export default AttachedFiles
